/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable prettier/prettier */
/* eslint-disable max-len */

import type { BackOfficeCardStatusDtoV1, CardCategory } from '@api/swagger/definitions/backoffice/models'
import { CardAuditApiFactory } from '@mynt/backoffice-api-spec/card-audit'
import { CardWalletsApiFactory } from '@mynt/backoffice-api-spec/card-wallet'
import { EndOfDayStatementsApiFactory } from '@mynt/backoffice-api-spec/end-of-day-statements'
import type { CreateInvoiceRequest, InvoicePatch, InvoicePdfResponse, InvoiceState, InvoiceType } from '@mynt/backoffice-api-spec/invoice'
import { InvoiceApiFactory } from '@mynt/backoffice-api-spec/invoice'
import { PaymentInstructionApiFactory } from '@mynt/backoffice-api-spec/payment'
import type { AxiosPromise, AxiosRequestConfig } from 'axios'
import config from '../config'
import { normalizeString } from '../helpers/common'
import type {
  BackOfficeEnfucePayoutStateV3,
  BackOfficePayoutCreateDtoV3,
  BackOfficePayoutDataResponseDtoV3,
  BackOfficePayoutPatchDtoV3,
} from '../types/payoutV3'
import Axios from './axios'
import type { SmsBody } from './react-query'
import type { ObjectWithId } from './react-query/interfaces/mynt'
import {
  type AccountDto,
  type AccountXYear,
  type ActivationGuidePatchDto,
  type AttestStatus,
  type BackOfficeAccountPatchDto,
  type BackOfficeAgreementCreationDtoV8,
  type BackOfficeAgreementTypeV8,
  BackOfficeAPIApiFactory,
  type BackOfficeCardCreditPatchV2Dto,
  type BackOfficeCreatePaymentForCustomerRequestDto,
  type BackOfficeCustomerCreateDtoV2,
  type BackOfficeCustomerPatchDtoV2,
  type BackOfficePatchPaymentRequestDto,
  type BackofficeInvoicingConfigPatchDto,
  type BureauConfigRequest,
  type BureauRelationshipRequest,
  type BureauUnassingmentRequest,
  type CardBalanceConfigPatchDto,
  type CreateDirectDebitMandateV4Request,
  type CreatePaymentRequestDtoV2,
  type CreatePersonDto,
  type FileContext,
  type MonthlyReportRequest,
  type PatchPaymentRequestDtoV2,
  type PatchPrepaidResourceBody,
  type PatchStandardResourceBody,
  type PaymentRequestStatusDtoV2,
  type PaymentState,
  type PersonDto,
  type PersonPatchDto,
  type ResetRequest,
  type TransactionFilters,
  type VoucherDto,
} from './swagger/definitions/backoffice'

export const backofficeApi = BackOfficeAPIApiFactory(undefined, config.boBaseUrl, Axios)
export const invoiceApi = InvoiceApiFactory(undefined, config.boBaseUrl, Axios)

export const cardAuditApi = CardAuditApiFactory(undefined, config.boBaseUrl, Axios)
export const cardWalletApi = CardWalletsApiFactory(undefined, config.boBaseUrl, Axios)

export const endOfDayStatementsApi = EndOfDayStatementsApiFactory(undefined, config.boBaseUrl, Axios)
export const paymentsApi = PaymentInstructionApiFactory(undefined, config.boBaseUrl, Axios)

// Verify payment
export const getPayment = (paymentId: string) => backofficeApi.getPayment(paymentId).then(({ data }) => data)
export const getPaymentReceiver = (paymentId: string) => backofficeApi.getPaymentReceiverV1(paymentId).then(({ data }) => data)
export const getPaymentWhitelist = (customerId: string) => backofficeApi.getPaymentWhiteListV1(customerId).then(({ data }) => data)
export const addPaymentToWhiteList = (paymentId: string) => backofficeApi.whiteListPaymentV1(paymentId).then(({ data }) => data)
export const removePaymentToWhiteList = (customerId: string, paymentWhitelistId: string) =>
  backofficeApi.deletePaymentWhiteListV1(customerId, paymentWhitelistId).then(({ data }) => data)

// bureau
export const bureauConfigure = (payload: BureauConfigRequest) => backofficeApi.configure(payload).then(({ data }) => data)
export const bureauAssign = (payload: BureauRelationshipRequest) => backofficeApi.assign(payload).then(({ data }) => data)
export const bureauUnassign = (payload: BureauUnassingmentRequest) => backofficeApi.unassign(payload).then(({ data }) => data)
export const bureauList = () => backofficeApi.list().then(({ data }) => data)
export const bureauReset = (payload: ResetRequest) =>
  backofficeApi.resetBureau(payload, { disableCustomErrorHandler: true }).then(({ data }) => data)
export const bureauClients = (bureauId: string) =>
  backofficeApi.bureauClients(bureauId, { disableCustomErrorHandler: true }).then(({ data }) => data)
export const bureau = (bureauId: string) => backofficeApi.bureau(bureauId, { disableCustomErrorHandler: true }).then(({ data }) => data)
export const recalculatePaumentRequests = async (creditId: string) => backofficeApi.postRecalculateV2(creditId).then(({ data }) => data)
export const createPaymentRequest = async (paymentRequestDto: CreatePaymentRequestDtoV2) =>
  backofficeApi.createV2(paymentRequestDto).then(({ data }) => data)
export const patchPaymentRequest = async (paymentRequestId: string, patchPaymentRequestDtoV2: PatchPaymentRequestDtoV2) =>
  backofficeApi.patchPaymentRequestV2(paymentRequestId, patchPaymentRequestDtoV2).then(({ data }) => data)

export const getCustomerLog = (customerId: string) => backofficeApi.log(customerId).then(({ data }) => data)
export const getCustomerNotes = (customerId: string) => backofficeApi.findBy(customerId).then(({ data }) => data)
export const getInvoiceAuditLog = async (customerId: string, invoiceId: string) =>
  backofficeApi.findByCustomerId(customerId, invoiceId).then(({ data }) => data)
export const getInvoiceConfig = async (customerId: string) => backofficeApi.getInvoicingConfigV1(customerId).then(({ data }) => data)
export const patchInvoiceConfig = async (customerId: string, payload: BackofficeInvoicingConfigPatchDto) =>
  backofficeApi.patchInvoicingConfigV1(customerId, payload).then(({ data }) => data)
export const patchCardV1 = async (
  customerId: string,
  cardId: string,
  {
    singleAmountLimit,
    dailySumAmount,
    personId,
    category,
    status,
  }: Partial<{
    singleAmountLimit: number
    dailySumAmount: number
    personId: string
    category: CardCategory
    status: BackOfficeCardStatusDtoV1
  }>,
) =>
  backofficeApi
    .patchCardV1(customerId, cardId, { singleAmountLimit, _24HourSumAmount: dailySumAmount, newCardHolder: personId, category, status })
    .then(({ data }) => data)
export const getActivationGuide = async (customerId: string) => backofficeApi.getActivationGuide(customerId).then(({ data }) => data)
export const createSmsTemplate = async (body: Partial<SmsBody>) => backofficeApi.add(body).then(({ data }) => data)
export const deleteSmsTemplate = async (id: string) => backofficeApi._delete(id).then(({ data }) => data)
export const updateSmsTemplate = async (id: string, body: SmsBody) => backofficeApi.put(id, body).then(({ data }) => data)
export const sendSms = async (customerId: string, personId: string, text: string) =>
  backofficeApi.sendSms(customerId, personId, { text }).then(({ data }) => data)
export const getSmsTemplates = async () => backofficeApi.findAll().then(({ data }) => data)
export const search = async (query: string, maxRows?: number, type?: string) =>
  backofficeApi.search(query, maxRows, type).then(({ data }) => data)
export const getPresignedFileURL = async (customerId: string, fileId: string) =>
  backofficeApi.getPreSignedUrl(customerId, fileId).then(({ data }) => data)
export const syncScoringFile = async (customerId: string, id: string, context: FileContext) =>
  backofficeApi.syncFile(customerId, id, { context }).then(({ data }) => data)
export const getScoringFileUploadURL = async (customerId: string, context: FileContext) =>
  backofficeApi.addFile(customerId, { context }).then(({ data }) => data)
export const uploadScoringFile = async (url: string, file: File) =>
  (
    Axios.put(url, file, {
      headers: { 'Content-Type': file.type, 'Content-Disposition': `filename="${normalizeString(file.name)}"` },
    }) as AxiosPromise<any>
  ).then(({ data }) => data)
export const getCustomerFiles = async (customerId: string, contexts: string[]) =>
  backofficeApi.getFiles(customerId, contexts).then(({ data }) => data)
export const deleteCustomerFile = async (customerId: string, fileId: string) =>
  backofficeApi.deleteFile(customerId, fileId).then(({ data }) => data)
export const updateCardAccount = async (customerId: string, body: PatchPrepaidResourceBody) =>
  backofficeApi.patchAccount(customerId, body).then(({ data }) => data)
export const updateCustomerCardAccount = async (customerId: string, accountId: string, body: BackOfficeAccountPatchDto) =>
  backofficeApi.patchAccountV2(customerId, accountId, body).then(({ data }) => data)
export const getAccountingYears = async () => backofficeApi.getYears().then(({ data }) => data)
export const getAccountingAccounts = async (year?: string) => backofficeApi.getAccounts(year).then(({ data }) => data)
export const getCustomerCardAccounts = async (customerId: string) => backofficeApi.getAccountsV2(customerId).then(({ data }) => data)
export const getVoucherResult = async (from?: string, to?: string, years?: string[], accounts?: number[]) =>
  backofficeApi.getVouchersResultReport(from, to, years, accounts).then(({ data }) => data)
export const getAllVouchers = async (from?: string, to?: string, years?: string[], accounts?: number[]) =>
  backofficeApi.getAllVouchers(from, to, years, accounts).then(({ data }) => data)
export const createVoucher = async (voucherDto: VoucherDto) => backofficeApi.createVoucher(voucherDto).then(({ data }) => data)
export const createAccountingYear = async (yearDto: AccountXYear) => backofficeApi.createYear(yearDto).then(({ data }) => data)
export const editAccountingYear = async (id: string, yearDto: AccountXYear) =>
  backofficeApi.updateYear(id, yearDto).then(({ data }) => data)
export const createAccountingAccount = async (accountDto: AccountDto) => backofficeApi.createAccount(accountDto).then(({ data }) => data)
export const updateAccountingAccount = async (id: string, accountDto: AccountDto) =>
  backofficeApi.updateAccount(id, accountDto).then(({ data }) => data)
export const deleteAccountingAccount = async (id: string) => backofficeApi.deleteAccount(id).then(({ data }) => data)
export const deleteAccountingYear = async (id: string) => backofficeApi.deleteYear(id).then(({ data }) => data)

export const getActivationVisibility = async (customerId: string) =>
  backofficeApi.getActivationGuideVisibility(customerId).then(({ data }) => data)
export const patchActivationVisibility = async (customerId: string, activationGuidePatchDto: ActivationGuidePatchDto) =>
  backofficeApi.patchActivationGuideVisibility(customerId, activationGuidePatchDto).then(({ data }) => data)
export const getTransactions = async (
  customerId: string,
  { page, offset, limit }: { page?: number; offset?: number; limit?: number } = {},
) => backofficeApi.getTransactionsCombined(customerId, page, offset, limit).then(({ data }) => data)
export const updateCustomer = async (customerId: string, dto: BackOfficeCustomerPatchDtoV2) =>
  backofficeApi.patchCustomer(customerId, dto).then(({ data }) => data)
export const getCards = async (customerId: string) => backofficeApi.getCards(customerId).then(({ data }) => data)
export const getCardsV2 = async (customerId: string) => backofficeApi.cards(customerId).then(({ data }) => data)
export const getCardAudit = async (customerId: string, cardId: string) =>
  cardAuditApi.getCardAuditLog(customerId, cardId).then(({ data }) => data)
export const getCardTokens = async (customerId: string, cardId: string) =>
  cardWalletApi.getCardWalletsTokens(customerId, cardId).then(({ data }) => data)
export const getPrivateCustomerCard = async (customerId: string, cardId: string) =>
  backofficeApi.getCard(customerId, cardId).then(({ data }) => data)
export const getPrivateCustomerCardPlastics = async (customerId: string, cardId: string) =>
  backofficeApi.getCardPlastics(customerId, cardId).then(({ data }) => data)
export const getPrivateCustomerCardConfig = async (customerId: string, cardId: string) =>
  backofficeApi.getCardConfig(customerId, cardId).then(({ data }) => data)

export const activatePlastic = async (customerId: string, cardId: string, plasticId: string) =>
  backofficeApi.activatePlastic(customerId, cardId, plasticId).then(({ data }) => data)
export const getCardDeliveryAddress = async (customerId: string, personId: string) =>
  backofficeApi.cardDeliveryAddress(customerId, personId).then(({ data }) => data)

export const transactionTriggerBusinessEventUsingPOST = async (customerId: string, transactionId: string) =>
  backofficeApi.triggerBusinessEvent(customerId, transactionId).then(({ data }) => data)

export const patchPerson = async (body: Partial<any>, personId: string, customerId: string) =>
  backofficeApi.updatePerson(customerId, personId, body).then(({ data }) => data)
export const postPEReports = async (customerId: string, body: MonthlyReportRequest) => backofficeApi.sendMonthlyReport(customerId, body)
export const getPEReports = async (customerId: string) => backofficeApi.getAllMonthlyReports(customerId).then(({ data }) => data)

export const getPersonsWithRolesUsingGET = async (customerId: string) =>
  backofficeApi.getPersonsWithRoles(customerId).then(({ data }) => data as Required<PersonDto>[])
export const getPeopleByCustomerId = getPersonsWithRolesUsingGET

export const createCustomer = async (customerDto: BackOfficeCustomerCreateDtoV2) =>
  backofficeApi.createCustomer(customerDto).then(({ data }) => data)
export const getGermanDistrictCourtCodes = async () => backofficeApi.getGermanyDistrictCourtCodes().then(({ data }) => data)
export const patchCardConfig = async (customerId: string, cardId: string, config: Record<string, unknown> | any) =>
  backofficeApi.patchCardConfig(customerId, cardId, config).then(({ data }) => data)
export const reissueCard = async (customerId: string, cardId: string) =>
  backofficeApi.reissueCard(customerId, cardId).then(({ data }) => data)
export const resetCardPin = async (customerId: string, cardId: string) =>
  backofficeApi.resetPinAttempt(customerId, cardId).then(({ data }) => data)
export const patchCard = async (customerId: string, cardId: string, req: PatchStandardResourceBody) =>
  backofficeApi.patchCard(customerId, cardId, req).then(({ data }) => data)
export const deletePreorderCard = async (customerId: string, cardId: string) =>
  backofficeApi.deletePreorderCardV1(customerId, cardId).then(({ data }) => data)
export const patchCardBalanceConfigUsingPATCH = async (req: CardBalanceConfigPatchDto, customerId: string) =>
  backofficeApi.patchCardBalanceConfig(customerId, req).then(({ data }) => data)
export const getOrCreateCardBalanceConfigUsingGET = async (customerId: string) =>
  backofficeApi.getCardBalanceConfig(customerId).then(({ data }) => data)
export const getAllEnfucePayoutsV3 = async (customerId?: string, states?: BackOfficeEnfucePayoutStateV3[]) =>
  Axios.get('/bo/v3/payouts', { params: { customerId, states }, paramsSerializer: { indexes: null } }).then(({ data }) => data) as Promise<
    BackOfficePayoutDataResponseDtoV3[]
  >
export const createEnfucePayoutUsingPOST = async (req: BackOfficePayoutCreateDtoV3) =>
  Axios.post('/bo/v3/payouts', req).then(({ data }) => data)
export const patchEnfucePayoutV3 = async ({ payoutId, payout }: { payoutId: string; payout: BackOfficePayoutPatchDtoV3 }) =>
  Axios.patch(`/bo/v3/payouts/${payoutId}`, payout).then(({ data }) => data) as Promise<BackOfficePayoutPatchDtoV3>
export const patchPaymentUsingPATCH = async ({ paymentId, payout }: { paymentId: string; payout: BackOfficePatchPaymentRequestDto }) =>
  backofficeApi.patchPayment(paymentId, payout).then(({ data }) => data) as Promise<BackOfficePatchPaymentRequestDto>
export const getPaymentsUsingGET = async (state?: PaymentState[]) => backofficeApi.getPayments(state).then(({ data }) => data)

export const directLoginTokenUsingPOST = async (customerId: string, personId: string) =>
  backofficeApi.directLoginToken(customerId, personId).then(({ data }) => data as unknown as string)
export const getCardCreditsUsingGET = async (customerId: string) => backofficeApi.getCardCreditsV2(customerId).then(({ data }) => data)

export const searchCreditApplicationsUsingGET = async (customerId: string) =>
  backofficeApi.searchCreditApplications(undefined, customerId).then(({ data }) => data)

export const patchCardCreditUsingPATCH = async (customerId: string, creditId: string, req: BackOfficeCardCreditPatchV2Dto, options?: any) =>
  backofficeApi.patchCardCreditV2(customerId, creditId, req, options).then(({ data }) => data)

export const getInvoicesV2 = async (
  customerId?: string,
  {
    type,
    state,
    from,
    to,
    settleFrom,
    settleTo,
  }: Partial<{
    type: InvoiceType[]
    state: InvoiceState[]
    from: string
    to: string
    settleFrom: string
    settleTo: string
  }> = {},
) => invoiceApi.listInvoicesPaginated(customerId, type, state, from, to, settleFrom, settleTo).then(({ data }) => data)
export const createInvoice = async (invoice: CreateInvoiceRequest) => invoiceApi.createInvoice(invoice).then(({ data }) => data)
export const updateInvoice = async (invoiceDto: InvoicePatch & ObjectWithId) =>
  invoiceApi.updateInvoice(invoiceDto.id, invoiceDto).then(({ data }) => data)
export const getDefaultPenaltyFee = async (customerId: string) => invoiceApi.getDefaultPenaltyFee(customerId).then(({ data }) => data)

export const findCustomerUsingGET2 = async (customerId: string) => backofficeApi.findCustomerById(customerId).then(({ data }) => data)
export const getDownloadablePdfUrl = async (url: string) => Axios.get<InvoicePdfResponse>(url).then(({ data }) => data)
export const deletePersonUsingDELETE = async (customerId: string, personId: string) =>
  backofficeApi.deletePerson(customerId, personId).then(({ data }) => data)
export const deleteCustomerUsingDELETE = async (customerId: string) => backofficeApi.deleteCustomer(customerId)
export const churnCustomerUsingPOST = async (customerId: string) => backofficeApi.churnCustomer(customerId)
export const registerExternalRepayment = async (req: BackOfficeCreatePaymentForCustomerRequestDto) =>
  backofficeApi.createPayment({ ...req }).then(({ data }) => data)
export const updatePersonUsingPATCH = async (customerId: string, personId: string, personDto: PersonPatchDto) =>
  backofficeApi.updatePerson(customerId, personId, personDto).then(({ data }) => data)
export const createPersonUsingPOST = async (customerId: string, personDto: CreatePersonDto) =>
  backofficeApi.createPersonWithRoles(customerId, personDto).then(({ data }) => data)
export const createNote = async (customerId: string, text: string) => backofficeApi.add1(customerId, { text }).then(({ data }) => data)
export const updateNote = async (customerId: string, noteId: string, text: string, rev: number) =>
  backofficeApi.put1(customerId, noteId, { text, rev }).then(({ data }) => data)
export const deleteNote = async (customerId: string, noteId: string) => backofficeApi.delete1(customerId, noteId).then(({ data }) => data)
export const snoozeInboxItem = async (itemId: string, until: string) => backofficeApi.addSnooze(itemId, { until }).then(({ data }) => data)
export const unSnoozeInboxItem = async (itemId: string) => backofficeApi.deleteSnooze(itemId).then(({ data }) => data)
export const getInbox = async () => backofficeApi.getInbox().then(({ data }) => data)

export const getAllAgreementsV8 = async (customerId: string, agreementType: BackOfficeAgreementTypeV8[]) =>
  backofficeApi.getAllAgreementsV8(customerId, agreementType).then(({ data }) => data)
export const createAgreementV8 = async (customerId: string, agreementDto: BackOfficeAgreementCreationDtoV8) =>
  backofficeApi.createAgreementV8(customerId, agreementDto).then(({ data }) => data)

export const syncCards = async (customerId: string, cardId: string) => backofficeApi.syncCard(customerId, cardId).then(({ data }) => data)

export const getDirectDebitMandatesV4 = async (customerId: string) =>
  backofficeApi.getDirectDebitMandatesV4(customerId).then(({ data }) => data)
export const createDirectDebitMandateV4 = async (customerId: string, req: CreateDirectDebitMandateV4Request) =>
  backofficeApi.createDirectDebitMandateV4(customerId, req)
export const syncDirectDebitMandateV4 = async (customerId: string, mandateId: string) =>
  backofficeApi.syncDirectDebitMandateV4(customerId, mandateId).then(({ data }) => data)

export const getPreferredMandateV4 = async (customerId: string) =>
  backofficeApi.getPreferredDirectDebitMandateV4(customerId).then(({ data }) => data)
export const getRefreshAddress = async (customerId: string) => backofficeApi.refreshAddress(customerId).then(({ data }) => data)

export const cancelDirectDebitMandateV4 = async (customerId: string, mandateId: string) =>
  backofficeApi.cancelDirectDebitMandateV4(customerId, mandateId).then(({ data }) => data)
export const resendDirectDebitMandateV4 = async (customerId: string, mandateId: string) =>
  backofficeApi.resendDirectDebitMandateV4(customerId, mandateId).then(({ data }) => data)

export const getSignatories = async (customerId: string) => backofficeApi.getSignatories(customerId).then(({ data }) => data)

export const sendWelcomeEmail = async (customerId: string) => backofficeApi.sendWelcomeEmail(customerId).then(({ data }) => data)

// Auth
export const signInWithGoogle = (redirect: string) =>
  backofficeApi.googleOAuthRedir(redirect, {
    adapter: ({ baseURL, url }): AxiosPromise => {
      window.open(`${baseURL}${url}`, '_self')
      // window.open(_, '_self') navigates the current tab away, so no response
      // ever arrives. Return a never-settling promise instead of the Window
      // object: axios calls adapter(config).then(...), and a non-Promise return
      // throws "adapter(...).then is not a function" as an unhandled rejection.
      return new Promise<never>(() => {})
    },
  })

export const getSignInType = (axiosOptions?: AxiosRequestConfig) => backofficeApi.isLoggedIn(axiosOptions).then(({ data }) => data)
export const signOut = () => backofficeApi.logout().then(({ data }) => data)

// Dev
export const switchToDevSalesRole = () =>
  window.open(`${config.boBaseUrl}/bo/dev-switch-to-sales?callback=${window.location.href}`, '_self')
export const switchToDevAdminRole = () =>
  window.open(`${config.boBaseUrl}/bo/dev-switch-to-backoffice?callback=${window.location.href}`, '_self')

// Big boy requests
export const getTransactionsV2 = async (
  customerId: string,
  {
    offset,
    limit,
    from,
    to,
    cardIds,
    exclude,
    attestStatus,
    max,
    min,
    onlyinclude,
    personIds,
    query,
  }: {
    offset?: number
    limit?: number
    from?: string
    to?: string
    min?: number
    max?: number
    onlyinclude?: TransactionFilters[]
    exclude?: TransactionFilters[]
    attestStatus?: AttestStatus[]
    query?: string
    personIds?: string[]
    cardIds?: string[]
  } = {},
) =>
  backofficeApi
    .getBoTransactionsV2(customerId, offset, limit, from, to, min, max, onlyinclude, exclude, attestStatus, query, personIds, cardIds)
    .then(({ data }) => data)

export const getPaymentRequests = async (
  customerId: string | undefined,
  filters: {
    cardCreditId?: string
    currency?: string[]
    hasPaidEnough?: boolean
    status?: PaymentRequestStatusDtoV2[]
    dueDateFrom?: string
    dueDateTo?: string
    paymentRequestDateFrom?: string
    paymentRequestDateTo?: string
    periodFrom?: string
    periodTo?: string
  },
  options?: any,
) =>
  backofficeApi
    .getPaymentRequestsV2(
      customerId,
      filters.cardCreditId,
      filters.currency,
      filters.hasPaidEnough,
      filters.status,
      filters.dueDateFrom,
      filters.dueDateTo,
      filters.paymentRequestDateFrom,
      filters.paymentRequestDateTo,
      filters.periodFrom,
      filters.periodTo,
      options,
    )
    .then(({ data }) => data)

export const getEndOfDayStatements = async (pagination: { page: number; pageSize: number } = { page: 1, pageSize: 25 }) =>
  endOfDayStatementsApi.listEndOfDayStatements(pagination.page, pagination.pageSize).then(({ data }) => data)

export const getPaymentOrders = async (pagination: { page: number; pageSize: number } = { page: 1, pageSize: 25 }) =>
  paymentsApi.listPaymentOrdersV2(pagination.page, pagination.pageSize).then(({ data }) => data)

export const markPaymentOrderAmlApproved = async (id: string) => paymentsApi.markAmlApproved(id).then(({ data }) => data)
